import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, CircularProgress, Stack, Typography } from '@mui/material';
import SEOPageTitle from 'components/shared/SEOPageTitle';
import AuthScreen from 'components/auth/portal/AuthScreen';
import StyledNavigationLink from 'common/theme/links/StyledNavigationLink';
import { homePathByRole } from 'common/constants/general';
import routes from 'common/routes';
import auth from 'api/graphql/auth';
import authSlice from 'store/modules/auth';
import requestStatus from 'common/constants/status';

function VerifyEmail() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation(['auth']);
  const [searchParams] = useSearchParams();
  const hasVerified = useRef(false);
  const [resendStatus, setResendStatus] = useState('idle');

  const status = useSelector((state) => state.auth.status);
  const user = useSelector((state) => state.auth.user);

  const token = searchParams.get('token') || '';
  const email = searchParams.get('email') || '';

  useEffect(() => {
    if (token && email && !hasVerified.current) {
      hasVerified.current = true;
      dispatch(authSlice.actions.verifyEmail({ token, email }));
    }
  }, [dispatch, token, email]);

  useEffect(() => {
    if (user) {
      const path = homePathByRole[user.role] || homePathByRole.default;
      navigate(path, { replace: true });
    }
  }, [navigate, user]);

  const handleResend = async () => {
    if (!email || resendStatus === 'loading') return;
    setResendStatus('loading');
    try {
      const response = await auth.resendVerificationEmail(email);
      const { sent } = response.data.resendVerificationEmail;
      setResendStatus(sent ? 'sent' : 'error');
    } catch {
      setResendStatus('error');
    }
  };

  const isLoading = status === requestStatus.loading;
  const isError = status === requestStatus.error;

  return (
    <>
      <SEOPageTitle title={t('verifyEmail.title', { ns: 'auth' })} />
      <AuthScreen>
        <Stack spacing={3} useFlexGap sx={{ width: '100%', alignItems: 'center' }}>
          {isLoading && (
            <Stack spacing={2} alignItems="center">
              <CircularProgress color="secondary" />
              <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                {t('verifyEmail.verifying', { ns: 'auth' })}
              </Typography>
            </Stack>
          )}

          {isError && (
            <Stack spacing={2} sx={{ width: '100%' }}>
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {t('verifyEmail.error', { ns: 'auth' })}
              </Alert>

              {resendStatus === 'sent' && (
                <Alert severity="success" sx={{ borderRadius: 2 }}>
                  {t('verifyEmail.resendSuccess', { ns: 'auth' })}
                </Alert>
              )}
              {resendStatus === 'error' && (
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                  {t('verifyEmail.resendError', { ns: 'auth' })}
                </Alert>
              )}

              {email && (
                <Button
                  variant="outlined"
                  onClick={handleResend}
                  disabled={resendStatus === 'loading' || resendStatus === 'sent'}
                  startIcon={resendStatus === 'loading' ? <CircularProgress size={16} /> : null}
                  sx={{ borderColor: 'hairline.strong', color: 'text.primary', textTransform: 'none' }}>
                  {t('verifyEmail.resendButton', { ns: 'auth' })}
                </Button>
              )}

              <StyledNavigationLink
                component={RouterLink}
                to={routes.login.path}
                variant="subtitle2"
                underline="always">
                {t('verifyEmail.loginLink', { ns: 'auth' })}
              </StyledNavigationLink>
            </Stack>
          )}
        </Stack>
      </AuthScreen>
    </>
  );
}

export default VerifyEmail;
